import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import {
  Container,
  Box,
  Typography,
  Paper,
  Grid,
  Button,
  Chip,
  List,
  ListItem,
  ListItemIcon,
  ListItemText,
  Card,
  CardMedia,
  Divider,
  Alert,
  CircularProgress,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  Stepper,
  Step,
  StepLabel,
  useTheme,
  useMediaQuery,
  IconButton
} from '@mui/material';
import {
  Check as CheckIcon,
  CalendarMonth as CalendarIcon,
  Schedule as ScheduleIcon,
  Group as GroupIcon,
  LocalOffer as PriceIcon,
  Place as PlaceIcon,
  ShoppingCart as CartIcon,
  EventAvailable as BookingIcon,
  CreditCard as PaymentIcon
} from '@mui/icons-material';
import { getPackageById, getVenueById, recordPackageView } from '../../services/venueService';
import { Package, Venue } from '../../types/Venue';
import BookingService from '../../services/bookingService';
import { useUser } from '../../contexts/UserContext';

const PublicPackageDetail: React.FC = () => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
  const { venueId, packageId } = useParams<{ venueId: string; packageId: string }>();
  const navigate = useNavigate();
  
  const [packageData, setPackageData] = useState<Package | null>(null);
  const [venueData, setVenueData] = useState<Venue | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [purchaseDialogOpen, setPurchaseDialogOpen] = useState(false);
  const [activeStep, setActiveStep] = useState(0);
  const [formData, setFormData] = useState({
    firstName: '',
    lastName: '',
    email: '',
    phone: '',
    date: '',
    timeSlot: '',
    guests: 2,
    specialRequests: ''
  });

  // Current timestamp for package availability checking
  const now = new Date();
  const currentDay = now.getDay(); // 0-6 for Sunday-Saturday
  
  // Purchase steps
  const steps = ['Select Date & Time', 'Customer Information', 'Review & Payment', 'Confirmation'];

  useEffect(() => {
    const fetchData = async () => {
      if (!venueId || !packageId) {
        setError('Invalid venue or package ID');
        setLoading(false);
        return;
      }

      try {
        // Fetch package details
        const packageDetails = await getPackageById(venueId, packageId);
        setPackageData(packageDetails);
        
        // Fetch venue details
        const venueDetails = await getVenueById(venueId);
        setVenueData(venueDetails);
        
        // Record package view for analytics
        recordPackageView(venueId, packageId);
        
      } catch (err) {
        console.error('Error fetching data:', err);
        setError('Failed to load package details. Please try again.');
      } finally {
        setLoading(false);
      }
    };

    fetchData();
  }, [venueId, packageId]);

  const handlePurchaseClick = () => {
    setPurchaseDialogOpen(true);
  };

  const handlePurchaseClose = () => {
    setPurchaseDialogOpen(false);
    setActiveStep(0);
  };

  const handleNext = () => {
    setActiveStep((prevActiveStep) => prevActiveStep + 1);
  };

  const handleBack = () => {
    setActiveStep((prevActiveStep) => prevActiveStep - 1);
  };

  const handleFormChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const { name, value } = e.target;
    setFormData({
      ...formData,
      [name]: value
    });
  };

  const handleTimeSlotSelect = (timeSlot: string) => {
    setFormData({
      ...formData,
      timeSlot
    });
  };

  const { currentUser } = useUser();

  // Payment processing state
  const [paymentLoading, setPaymentLoading] = useState(false);
  const [paymentError, setPaymentError] = useState<string | null>(null);
  const [paymentSuccess, setPaymentSuccess] = useState(false);
  const [bookingId, setBookingId] = useState<string | null>(null);

  const handleSubmitPurchase = async () => {
    if (!currentUser) {
      alert('You must be logged in to make a purchase');
      return;
    }

    if (!packageData || !venueId || !packageId) return;

    try {
      setPaymentLoading(true);
      setPaymentError(null);

      // Simulate a successful payment
      const simulatedPaymentId = `pay_${Date.now()}`;

      // Create the booking in Firestore
      const booking = await BookingService.createBooking(
        venueId,
        packageId,
        packageData,
        currentUser,
        {
          date: formData.date,
          timeSlot: formData.timeSlot,
          guests: formData.guests,
          specialRequests: formData.specialRequests,
          phone: formData.phone
        },
        simulatedPaymentId
      );

      setBookingId(booking);
      setPaymentSuccess(true);
      
      // Proceed to confirmation after a short delay
      setTimeout(() => {
        setActiveStep(3); // Move to confirmation step
      }, 1500);
    } catch (error) {
      console.error('Error processing payment:', error);
      setPaymentError('There was an error processing your payment. Please try again.');
    } finally {
      setPaymentLoading(false);
    }
  };

  const isPackageAvailableToday = () => {
    if (!packageData?.availability?.daysAvailable) return false;
    return packageData.availability.daysAvailable.includes(currentDay);
  };

  // Format time from 24h to 12h format
  const formatTime = (time: string) => {
    try {
      const [hours, minutes] = time.split(':');
      const hour = parseInt(hours);
      const ampm = hour >= 12 ? 'PM' : 'AM';
      const hour12 = hour % 12 || 12;
      return `${hour12}:${minutes} ${ampm}`;
    } catch (e) {
      return time;
    }
  };

  const renderTimeSlots = () => {
    if (!packageData?.availability?.timeSlots || packageData.availability.timeSlots.length === 0) {
      return <Typography variant="body2">No specific time slots available</Typography>;
    }

    return (
      <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1 }}>
        {packageData.availability.timeSlots.map((slot, index) => (
          <Chip
            key={index}
            label={`${formatTime(slot.start)} - ${formatTime(slot.end)}`}
            variant="outlined"
            color={isPackageAvailableToday() ? "primary" : "default"}
            size="small"
          />
        ))}
      </Box>
    );
  };

  // Renders the availability days as chips
  const renderAvailabilityDays = () => {
    if (!packageData?.availability?.daysAvailable || packageData.availability.daysAvailable.length === 0) {
      return <Typography variant="body2">Not currently available</Typography>;
    }

    const days = ['Sunday', 'Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday'];
    
    return (
      <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1 }}>
        {days.map((day, index) => (
          <Chip
            key={index}
            label={day}
            variant="outlined"
            color={packageData.availability.daysAvailable.includes(index) ? "primary" : "default"}
            size="small"
            sx={{
              opacity: packageData.availability.daysAvailable.includes(index) ? 1 : 0.5
            }}
          />
        ))}
      </Box>
    );
  };

  const renderPurchaseStepContent = () => {
    switch (activeStep) {
      case 0: // Date & Time selection
        return (
          <Box sx={{ py: 2 }}>
            <Typography variant="h6" gutterBottom>
              Select Date & Time
            </Typography>
            
            <Grid container spacing={3}>
              <Grid item xs={12} sm={6}>
                <TextField
                  fullWidth
                  label="Date"
                  type="date"
                  name="date"
                  value={formData.date}
                  onChange={handleFormChange}
                  InputLabelProps={{ shrink: true }}
                  required
                  sx={{ mb: 2 }}
                />
              </Grid>
              
              <Grid item xs={12} sm={6}>
                <TextField
                  fullWidth
                  label="Number of Guests"
                  type="number"
                  name="guests"
                  value={formData.guests}
                  onChange={handleFormChange}
                  InputLabelProps={{ shrink: true }}
                  InputProps={{ inputProps: { min: 1, max: packageData?.maxCapacity || 10 } }}
                  required
                  sx={{ mb: 2 }}
                />
              </Grid>
              
              <Grid item xs={12}>
                <Typography variant="subtitle1" gutterBottom>
                  Available Time Slots:
                </Typography>
                <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1 }}>
                  {packageData?.availability?.timeSlots?.map((slot, index) => (
                    <Chip
                      key={index}
                      label={`${formatTime(slot.start)} - ${formatTime(slot.end)}`}
                      variant={formData.timeSlot === `${slot.start}-${slot.end}` ? "filled" : "outlined"}
                      color="primary"
                      onClick={() => handleTimeSlotSelect(`${slot.start}-${slot.end}`)}
                      sx={{ mb: 1 }}
                    />
                  ))}
                </Box>
              </Grid>
              
              <Grid item xs={12}>
                <TextField
                  fullWidth
                  label="Special Requests"
                  name="specialRequests"
                  value={formData.specialRequests}
                  onChange={handleFormChange}
                  multiline
                  rows={3}
                  placeholder="Any special dietary requirements, seating preferences, or occasion notes..."
                />
              </Grid>
            </Grid>
          </Box>
        );
        
      case 1: // Customer Information
        return (
          <Box sx={{ py: 2 }}>
            <Typography variant="h6" gutterBottom>
              Your Information
            </Typography>
            
            <Grid container spacing={2}>
              <Grid item xs={12} sm={6}>
                <TextField
                  fullWidth
                  label="First Name"
                  name="firstName"
                  value={formData.firstName}
                  onChange={handleFormChange}
                  required
                />
              </Grid>
              
              <Grid item xs={12} sm={6}>
                <TextField
                  fullWidth
                  label="Last Name"
                  name="lastName"
                  value={formData.lastName}
                  onChange={handleFormChange}
                  required
                />
              </Grid>
              
              <Grid item xs={12} sm={6}>
                <TextField
                  fullWidth
                  label="Email"
                  name="email"
                  type="email"
                  value={formData.email}
                  onChange={handleFormChange}
                  required
                />
              </Grid>
              
              <Grid item xs={12} sm={6}>
                <TextField
                  fullWidth
                  label="Phone"
                  name="phone"
                  value={formData.phone}
                  onChange={handleFormChange}
                  required
                />
              </Grid>
            </Grid>
          </Box>
        );
        
      case 2: // Review & Payment
        return (
          <Box sx={{ py: 2 }}>
            <Typography variant="h6" gutterBottom>
              Review Your Booking
            </Typography>
            
            <Grid container spacing={2} sx={{ mb: 3 }}>
              <Grid item xs={12} sm={6}>
                <Paper sx={{ p: 2 }}>
                  <Typography variant="subtitle1" gutterBottom fontWeight="bold">
                    Booking Details
                  </Typography>
                  <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
                    <Box>
                      <Typography variant="body2" color="text.secondary">Date:</Typography>
                      <Typography variant="body1">{formData.date}</Typography>
                    </Box>
                    <Box>
                      <Typography variant="body2" color="text.secondary">Time:</Typography>
                      <Typography variant="body1">{formData.timeSlot}</Typography>
                    </Box>
                    <Box>
                      <Typography variant="body2" color="text.secondary">Guests:</Typography>
                      <Typography variant="body1">{formData.guests}</Typography>
                    </Box>
                    {formData.specialRequests && (
                      <Box>
                        <Typography variant="body2" color="text.secondary">Special Requests:</Typography>
                        <Typography variant="body1">{formData.specialRequests}</Typography>
                      </Box>
                    )}
                  </Box>
                </Paper>
              </Grid>
              
              <Grid item xs={12} sm={6}>
                <Paper sx={{ p: 2 }}>
                  <Typography variant="subtitle1" gutterBottom fontWeight="bold">
                    Contact Information
                  </Typography>
                  <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
                    <Box>
                      <Typography variant="body2" color="text.secondary">Name:</Typography>
                      <Typography variant="body1">{formData.firstName} {formData.lastName}</Typography>
                    </Box>
                    <Box>
                      <Typography variant="body2" color="text.secondary">Email:</Typography>
                      <Typography variant="body1">{formData.email}</Typography>
                    </Box>
                    <Box>
                      <Typography variant="body2" color="text.secondary">Phone:</Typography>
                      <Typography variant="body1">{formData.phone}</Typography>
                    </Box>
                  </Box>
                </Paper>
              </Grid>
            </Grid>
            
            <Paper sx={{ p: 2, mb: 3 }}>
              <Typography variant="subtitle1" gutterBottom fontWeight="bold">
                Price Summary
              </Typography>
              <Grid container spacing={1}>
                <Grid item xs={6}>
                  <Typography variant="body1">
                    Package Price:
                  </Typography>
                </Grid>
                <Grid item xs={6}>
                  <Typography variant="body1" align="right">
                    ${packageData?.price?.toFixed(2)}
                  </Typography>
                </Grid>
                
                <Grid item xs={6}>
                  <Typography variant="body2" color="text.secondary">
                    Tax (8%):
                  </Typography>
                </Grid>
                <Grid item xs={6}>
                  <Typography variant="body2" color="text.secondary" align="right">
                    ${((packageData?.price || 0) * 0.08).toFixed(2)}
                  </Typography>
                </Grid>
                
                <Grid item xs={6}>
                  <Typography variant="h6">
                    Total:
                  </Typography>
                </Grid>
                <Grid item xs={6}>
                  <Typography variant="h6" align="right" color="primary">
                    ${((packageData?.price || 0) * 1.08).toFixed(2)}
                  </Typography>
                </Grid>
              </Grid>
            </Paper>
            
            <Paper sx={{ p: 2 }}>
              <Typography variant="subtitle1" gutterBottom fontWeight="bold">
                Payment Method
              </Typography>
              
              {/* Simpler payment UI */}
              <Box sx={{ mb: 2, mt: 3, p: 2, border: '1px solid #e0e0e0', borderRadius: 1 }}>
                <Grid container spacing={2}>
                  <Grid item xs={12}>
                    <TextField
                      fullWidth
                      label="Card Number"
                      placeholder="4242 4242 4242 4242"
                      variant="outlined"
                      InputLabelProps={{ shrink: true }}
                    />
                  </Grid>
                  <Grid item xs={6}>
                    <TextField
                      fullWidth
                      label="Expiry Date"
                      placeholder="MM/YY"
                      variant="outlined"
                      InputLabelProps={{ shrink: true }}
                    />
                  </Grid>
                  <Grid item xs={6}>
                    <TextField
                      fullWidth
                      label="CVC"
                      placeholder="123"
                      variant="outlined"
                      InputLabelProps={{ shrink: true }}
                    />
                  </Grid>
                </Grid>
              </Box>
              
              {paymentError && (
                <Alert severity="error" sx={{ mt: 2, mb: 2 }}>
                  {paymentError}
                </Alert>
              )}
              
              {paymentSuccess && (
                <Alert severity="success" sx={{ mt: 2, mb: 2 }}>
                  Payment successful! Generating your booking confirmation...
                </Alert>
              )}
              
              <Button
                variant="contained"
                color="primary"
                fullWidth
                size="large"
                onClick={handleSubmitPurchase}
                disabled={paymentLoading || paymentSuccess}
                sx={{ mt: 2 }}
              >
                {paymentLoading ? (
                  <CircularProgress size={24} color="inherit" />
                ) : (
                  `Pay $${((packageData?.price || 0) * 1.08).toFixed(2)}`
                )}
              </Button>
              
              <Typography variant="caption" sx={{ display: 'block', mt: 1, textAlign: 'center' }}>
                Your card will be charged when you click the button above.
                <br />
                All payments are secure and encrypted.
              </Typography>
            </Paper>
          </Box>
        );

      case 3: // Confirmation
        return (
          <Box sx={{ py: 2, textAlign: 'center' }}>
            <CheckIcon color="success" sx={{ fontSize: 64, mb: 2 }} />
            
            <Typography variant="h5" gutterBottom>
              Booking Confirmed!
            </Typography>
            
            <Typography variant="body1" paragraph>
              Your booking for {packageData?.name} at {venueData?.name} has been confirmed.
            </Typography>
            
            <Paper sx={{ p: 3, mt: 3, mb: 3, maxWidth: 400, mx: 'auto', textAlign: 'left' }}>
              <Typography variant="subtitle1" gutterBottom fontWeight="bold">
                Booking Information
              </Typography>
              
              <Grid container spacing={2}>
                <Grid item xs={5}>
                  <Typography variant="body2" color="text.secondary">Booking ID:</Typography>
                </Grid>
                <Grid item xs={7}>
                  <Typography variant="body2" fontWeight="medium">{bookingId?.slice(0, 8)}...</Typography>
                </Grid>
                
                <Grid item xs={5}>
                  <Typography variant="body2" color="text.secondary">Date:</Typography>
                </Grid>
                <Grid item xs={7}>
                  <Typography variant="body2">{formData.date}</Typography>
                </Grid>
                
                <Grid item xs={5}>
                  <Typography variant="body2" color="text.secondary">Time:</Typography>
                </Grid>
                <Grid item xs={7}>
                  <Typography variant="body2">{formData.timeSlot}</Typography>
                </Grid>
                
                <Grid item xs={5}>
                  <Typography variant="body2" color="text.secondary">Guests:</Typography>
                </Grid>
                <Grid item xs={7}>
                  <Typography variant="body2">{formData.guests}</Typography>
                </Grid>
                
                <Grid item xs={5}>
                  <Typography variant="body2" color="text.secondary">Total Paid:</Typography>
                </Grid>
                <Grid item xs={7}>
                  <Typography variant="body2" fontWeight="medium">
                    ${((packageData?.price || 0) * 1.08).toFixed(2)}
                  </Typography>
                </Grid>
              </Grid>
            </Paper>
            
            <Box sx={{ mt: 3 }}>
              <Typography variant="body2" paragraph>
                A confirmation email has been sent to {formData.email}.
                <br />
                You can also find this booking in your account under "My Bookings".
              </Typography>
              
              <Button 
                variant="contained"
                onClick={() => navigate('/my-bookings')}
                sx={{ mr: 2 }}
              >
                View My Bookings
              </Button>
              
              <Button 
                variant="outlined"
                onClick={() => navigate('/explore/packages')}
              >
                Explore More Packages
              </Button>
            </Box>
          </Box>
        );
        
      default:
        return null;
    }
  };

  if (loading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '50vh' }}>
        <CircularProgress />
      </Box>
    );
  }

  if (error || !packageData || !venueData) {
    return (
      <Container maxWidth="lg" sx={{ py: 4 }}>
        <Alert severity="error">{error || 'Package or venue not found'}</Alert>
        <Button 
          onClick={() => navigate('/explore/packages')}
          sx={{ mt: 2 }}
        >
          Browse Other Packages
        </Button>
      </Container>
    );
  }

  return (
    <Container maxWidth="lg" sx={{ py: 4 }}>
      {/* Package Hero Section */}
      <Paper elevation={3} sx={{ overflow: 'hidden', mb: 4 }}>
        <Grid container>
          {/* Package Image */}
          <Grid item xs={12} md={6}>
            <Box sx={{ position: 'relative', height: '100%', minHeight: 300 }}>
              <CardMedia
                component="img"
                image={packageData.images[0] || 'https://images.unsplash.com/photo-1414235077428-338989a2e8c0?ixlib=rb-4.0.3&ixid=M3wxMjA3fDB8MHxwaG90by1wYWdlfHx8fGVufDB8fHx8fA%3D%3D&auto=format&fit=crop&w=1000&q=80'}
                alt={packageData.name}
                sx={{ height: '100%', minHeight: 300, objectFit: 'cover' }}
              />
              
              {/* Price Tag */}
              <Box 
                sx={{ 
                  position: 'absolute', 
                  bottom: 20, 
                  right: 20,
                  bgcolor: 'primary.main',
                  color: 'white',
                  py: 1,
                  px: 2,
                  borderRadius: 1,
                  fontWeight: 'bold',
                  boxShadow: 2
                }}
              >
                ${packageData.price?.toFixed(2)}
                {packageData.discountedPrice && (
                  <Typography 
                    component="span" 
                    sx={{ 
                      ml: 1, 
                      textDecoration: 'line-through', 
                      opacity: 0.7,
                      fontSize: '0.8em'
                    }}
                  >
                    ${packageData.discountedPrice.toFixed(2)}
                  </Typography>
                )}
              </Box>
            </Box>
          </Grid>
          
          {/* Package Details */}
          <Grid item xs={12} md={6}>
            <Box sx={{ p: 3 }}>
              {/* Venue Name */}
              <Typography variant="subtitle1" color="primary" gutterBottom>
                {venueData.name}
              </Typography>
              
              {/* Package Type */}
              <Chip 
                label={packageData.packageType.charAt(0).toUpperCase() + packageData.packageType.slice(1)} 
                size="small" 
                color="primary"
                variant="outlined"
                sx={{ mb: 1 }}
              />
              
              {/* Package Name */}
              <Typography variant="h4" component="h1" gutterBottom>
                {packageData.name}
              </Typography>
              
              {/* Package Description */}
              <Typography variant="body1" paragraph>
                {packageData.description}
              </Typography>
              
              {/* Location */}
              <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
                <PlaceIcon sx={{ color: 'text.secondary', mr: 1 }} />
                <Typography variant="body2">
                  {venueData.location.address}, {venueData.location.city}
                </Typography>
              </Box>
              
              {/* Package Details Grid */}
              <Grid container spacing={2} sx={{ mb: 3 }}>
                {/* Capacity */}
                <Grid item xs={6}>
                  <Box sx={{ display: 'flex', alignItems: 'center' }}>
                    <GroupIcon sx={{ color: 'text.secondary', mr: 1 }} />
                    <Typography variant="body2">
                      For up to {packageData.maxCapacity} {packageData.maxCapacity > 1 ? 'people' : 'person'}
                    </Typography>
                  </Box>
                </Grid>
                
                {/* Price */}
                <Grid item xs={6}>
                  <Box sx={{ display: 'flex', alignItems: 'center' }}>
                    <PriceIcon sx={{ color: 'text.secondary', mr: 1 }} />
                    <Typography variant="body2">
                      ${packageData.price?.toFixed(2)} per package
                    </Typography>
                  </Box>
                </Grid>
                
                {/* Available Days */}
                <Grid item xs={12}>
                  <Box sx={{ display: 'flex', alignItems: 'flex-start' }}>
                    <CalendarIcon sx={{ color: 'text.secondary', mr: 1, mt: 0.5 }} />
                    <Box>
                      <Typography variant="body2" gutterBottom>
                        Available Days:
                      </Typography>
                      {renderAvailabilityDays()}
                    </Box>
                  </Box>
                </Grid>
                
                {/* Time Slots */}
                <Grid item xs={12}>
                  <Box sx={{ display: 'flex', alignItems: 'flex-start' }}>
                    <ScheduleIcon sx={{ color: 'text.secondary', mr: 1, mt: 0.5 }} />
                    <Box>
                      <Typography variant="body2" gutterBottom>
                        Time Slots:
                      </Typography>
                      {renderTimeSlots()}
                    </Box>
                  </Box>
                </Grid>
              </Grid>
              
              {/* CTA Button */}
              <Button 
                variant="contained" 
                color="primary" 
                fullWidth
                startIcon={<CartIcon />}
                disabled={!packageData.isActive}
                onClick={handlePurchaseClick}
                sx={{ py: 1.5 }}
              >
                Purchase This Package
              </Button>
            </Box>
          </Grid>
        </Grid>
      </Paper>
      
      {/* Included Items Section */}
      <Paper elevation={2} sx={{ p: 3, mb: 4 }}>
        <Typography variant="h6" gutterBottom>
          What's Included
        </Typography>
        <List>
          {packageData.includesItems.map((item, index) => (
            <React.Fragment key={index}>
              {index > 0 && <Divider component="li" />}
              <ListItem>
                <ListItemIcon>
                  <CheckIcon color="success" />
                </ListItemIcon>
                <ListItemText primary={item} />
              </ListItem>
            </React.Fragment>
          ))}
        </List>
      </Paper>
      
      {/* Package Images Gallery */}
      {packageData.images.length > 1 && (
        <Paper elevation={2} sx={{ p: 3, mb: 4 }}>
          <Typography variant="h6" gutterBottom>
            Gallery
          </Typography>
          <Grid container spacing={2}>
            {packageData.images.slice(1).map((image, index) => (
              <Grid item xs={6} sm={4} md={3} key={index}>
                <Card sx={{ height: '100%' }}>
                  <CardMedia
                    component="img"
                    height={140}
                    image={image}
                    alt={`Gallery image ${index + 1}`}
                    sx={{ objectFit: 'cover' }}
                  />
                </Card>
              </Grid>
            ))}
          </Grid>
        </Paper>
      )}
      
      {/* Restrictions Section (if any) */}
      {packageData.restrictions && packageData.restrictions.length > 0 && (
        <Paper elevation={2} sx={{ p: 3, mb: 4 }}>
          <Typography variant="h6" gutterBottom>
            Restrictions & Requirements
          </Typography>
          <List dense>
            {packageData.restrictions.map((restriction, index) => (
              <ListItem key={index}>
                <ListItemText primary={restriction} />
              </ListItem>
            ))}
          </List>
        </Paper>
      )}
      
      {/* Purchase Dialog */}
      <Dialog
        open={purchaseDialogOpen}
        onClose={activeStep === steps.length - 1 ? undefined : handlePurchaseClose}
        maxWidth="md"
        fullWidth
      >
        <DialogTitle>
          {activeStep === steps.length - 1 ? 'Booking Confirmed' : `Book ${packageData.name}`}
          {activeStep < steps.length - 1 && (
            <IconButton
              aria-label="close"
              onClick={handlePurchaseClose}
              sx={{
                position: 'absolute',
                right: 8,
                top: 8,
                color: (theme: any) => theme.palette.grey[500]
              }}
            >
              ×
            </IconButton>
          )}
        </DialogTitle>
        <DialogContent>
          <Box sx={{ width: '100%' }}>
            <Stepper activeStep={activeStep} sx={{ py: 3 }}>
              {steps.map((label) => (
                <Step key={label}>
                  <StepLabel>{label}</StepLabel>
                </Step>
              ))}
            </Stepper>
            
            {renderPurchaseStepContent()}
          </Box>
        </DialogContent>
        <DialogActions>
          {activeStep < steps.length - 1 ? (
            <>
              <Button 
                onClick={handlePurchaseClose}
                sx={{ mr: 1 }}
              >
                Cancel
              </Button>
              <Button
                disabled={activeStep === 0}
                onClick={handleBack}
                sx={{ mr: 1 }}
              >
                Back
              </Button>
              {activeStep < 2 && (
                <Button 
                  variant="contained" 
                  color="primary"
                  onClick={handleNext}
                >
                  Next
                </Button>
              )}
            </>
          ) : (
            <Button 
              onClick={() => {
                handlePurchaseClose();
                // Navigate to explore packages page after closing
                navigate('/explore/packages');
              }}
            >
              Close
            </Button>
          )}
        </DialogActions>
      </Dialog>
    </Container>
  );
};

export default PublicPackageDetail;
